import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";

function parseTooltipText(raw) {
  if (!raw) return null;
  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
  const isBullet = (s) => s.startsWith("\u2022") || s.startsWith("o\t") || /^o\s{2,}/.test(s);

  let intro = "";
  const sections = [];
  let currentSection = null;
  let footer = "";
  let sawBullet = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isBullet(line)) {
      sawBullet = true;
      const text = line.replace(/^[\u2022o]\s*/, "").trim();
      if (!currentSection) {
        currentSection = { heading: null, items: [] };
        sections.push(currentSection);
      }
      currentSection.items.push(text);
      continue;
    }
    const nextHasBullets = lines.slice(i + 1).some(isBullet);
    if (sawBullet && !nextHasBullets) {
      footer = lines.slice(i).join(" ");
      break;
    }
    if (nextHasBullets) {
      currentSection = { heading: line, items: [] };
      sections.push(currentSection);
    } else {
      intro = intro ? intro + " " + line : line;
    }
  }

  if (!intro && !sections.length && !footer) return null;
  return { intro, sections, footer };
}

function ServiceCategoryTooltip({ name }) {
  const { rfis } = useSelector((state) => state.rfisColors);
  const rawTooltip = rfis?.find((r) => r.rfi_name === name)?.tooltip_description;
  const tooltip = useMemo(() => parseTooltipText(rawTooltip), [rawTooltip]);

  const iconRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, bottom: null, left: 0, maxHeight: 0 });
  const hideTimeout = useRef(null);

  const show = useCallback(() => {
    clearTimeout(hideTimeout.current);
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const maxWidth = Math.min(720, window.innerWidth - 16);
    let left = rect.left;
    if (left + maxWidth > window.innerWidth - 8) left = window.innerWidth - maxWidth - 8;
    if (left < 8) left = 8;

    const spaceBelow = window.innerHeight - rect.bottom - 16;
    const spaceAbove = rect.top - 16;
    const flipUp = spaceBelow < 240 && spaceAbove > spaceBelow;

    if (flipUp) {
      setPosition({
        top: null,
        bottom: window.innerHeight - rect.top + 8,
        left,
        maxHeight: spaceAbove,
      });
    } else {
      setPosition({
        top: rect.bottom + 8,
        bottom: null,
        left,
        maxHeight: spaceBelow,
      });
    }
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    hideTimeout.current = setTimeout(() => setVisible(false), 100);
  }, []);

  const keepVisible = useCallback(() => {
    clearTimeout(hideTimeout.current);
  }, []);

  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  if (!tooltip) return null;

  return (
    <>
      <span
        ref={iconRef}
        className="inline-flex items-center ml-[3px]"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <InformationCircleIcon
          className={`h-3.5 w-3.5 transition-opacity cursor-help ${
            visible ? "opacity-100" : "opacity-70 hover:opacity-100"
          }`}
        />
      </span>

      {visible &&
        createPortal(
          <div
            style={{
              top: position.top ?? undefined,
              bottom: position.bottom ?? undefined,
              left: position.left,
              position: "fixed",
            }}
            className="z-[99999] pointer-events-auto"
            onMouseEnter={keepVisible}
            onMouseLeave={hide}
          >
            <div
              style={{ maxHeight: position.maxHeight }}
              className="max-w-[720px] w-max overflow-y-auto overscroll-contain rounded-lg bg-white p-4 shadow-2xl border border-gray-200"
            >
              <h3 className="text-sm font-bold text-gray-900 mb-2">{name}</h3>

              {tooltip.intro && (
                <p className="text-xs text-gray-700 mb-2">{tooltip.intro}</p>
              )}

              {tooltip.sections?.map((section, i) => (
                <div key={i} className="mb-2">
                  {section.heading && (
                    <p className="text-xs font-bold text-gray-800 mb-1">
                      {section.heading}
                    </p>
                  )}
                  {section.items?.length > 0 && (
                    <ul className="list-disc list-inside space-y-0.5">
                      {section.items.map((item, j) => (
                        <li key={j} className="text-xs text-gray-600">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {tooltip.footer && (
                <p className="text-xs text-gray-500 mt-2 italic">{tooltip.footer}</p>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default ServiceCategoryTooltip;
